import {
  BarChart3,
  Boxes,
  ChevronLeft,
  CircleDollarSign,
  Factory,
  FileChartColumn,
  LayoutDashboard,
  LogOut,
  Menu,
  PackageSearch,
  Settings,
  Warehouse,
  Wrench,
  X
} from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../auth";
import { company } from "../company";
import { TableInteractionEnhancer } from "./TableInteractionEnhancer";

const menuItems = [
  { to: "/", label: "Trang chủ", icon: LayoutDashboard, permissions: ["Dashboard.View"] },
  { to: "/danh-muc", label: "Danh mục", icon: Boxes, permissions: ["MasterData.View"] },
  { to: "/kinh-doanh", label: "Kinh doanh", icon: BarChart3, permissions: ["Sales.View", "Purchasing.View"] },
  { to: "/san-xuat", label: "Sản xuất", icon: Factory, permissions: ["Planning.View", "Production.View"] },
  { to: "/thiet-bi", label: "Thiết bị", icon: Wrench, permissions: ["Equipment.View"] },
  { to: "/kho", label: "Kho", icon: Warehouse, permissions: ["Warehouse.View"] },
  { to: "/ke-toan", label: "Kế toán", icon: CircleDollarSign, permissions: ["Accounting.View"] },
  { to: "/truy-xuat", label: "Truy xuất", icon: PackageSearch, permissions: ["Traceability.View"] },
  { to: "/bao-cao", label: "Báo cáo", icon: FileChartColumn, permissions: ["Reports.View"] }
];

const pageTitles: Record<string, string> = {
  "/": "Trang chủ điều hành",
  "/danh-muc": "Danh mục dùng chung",
  "/kinh-doanh": "Kinh doanh",
  "/kho": "Kho",
  "/san-xuat": "Sản xuất",
  "/thiet-bi": "Thiết bị và linh kiện",
  "/ke-toan": "Kế toán",
  "/truy-xuat": "Truy xuất lô",
  "/bao-cao": "Báo cáo tổng hợp",
  "/he-thong/nguoi-dung": "Người dùng và phân quyền"
};

export function AppLayout({ children }: PropsWithChildren) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const [location] = useLocation();
  const isAdmin = user?.roles.includes("ADMIN") ?? false;
  const canView = (permissions: string[]) => isAdmin || permissions.some((permission) => user?.permissions.includes(permission));
  const title = location.startsWith("/danh-muc/san-pham/")
    ? "Chi tiết sản phẩm và BOM"
    : (pageTitles[location] ?? "GIA BẢO MEDICAL ERP");

  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <TableInteractionEnhancer />
      {mobileOpen && (
        <button
          className="mobile-backdrop"
          aria-label="Đóng menu"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="brand">
          <img className="brand-logo" src={company.logoPath} alt={`Logo ${company.brandName}`} />
          {!collapsed && (
            <div className="brand-copy">
              <strong>GIA BAO</strong>
              <span>MEDICAL ERP</span>
            </div>
          )}
          <button
            className="mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Đóng menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="main-nav" aria-label="Menu chính">
          {menuItems.filter((item) => canView(item.permissions)).map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              href={to}
              className={`nav-item ${location === to ? "active" : ""}`}
              title={collapsed ? label : undefined}
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={20} strokeWidth={1.9} />
              {!collapsed && <span>{label}</span>}
            </Link>
          ))}

          {isAdmin && <>
            <div className="nav-divider" />
            <Link
              href="/he-thong/nguoi-dung"
              className={`nav-item ${location === "/he-thong/nguoi-dung" ? "active" : ""}`}
              title={collapsed ? "Phân quyền" : undefined}
              onClick={() => setMobileOpen(false)}
            >
              <Settings size={20} strokeWidth={1.9} />
              {!collapsed && <span>Phân quyền</span>}
            </Link>
          </>}
        </nav>

        <button className="collapse-button" onClick={() => setCollapsed((value) => !value)}>
          <ChevronLeft size={18} />
          {!collapsed && <span>Thu gọn menu</span>}
        </button>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <div className="topbar-title">
            <button
              className="mobile-menu"
              onClick={() => setMobileOpen(true)}
              aria-label="Mở menu"
            >
              <Menu size={23} />
            </button>
            <div>
              <h1>{title}</h1>
              <p>Quản trị đơn giản – dữ liệu liên thông – truy xuất theo lô</p>
            </div>
          </div>
          <div className="user-area">
            <div className="user-avatar">{user?.displayName.slice(0, 1).toUpperCase()}</div>
            <div className="user-copy">
              <strong>{user?.displayName}</strong>
              <span>{user?.roles.join(", ")}</span>
            </div>
            <button className="icon-button" onClick={logout} title="Đăng xuất">
              <LogOut size={19} />
            </button>
          </div>
        </header>
        <main className="page-content">
          {children}
        </main>
      </div>
    </div>
  );
}
